import { Button, Group, Title, useMantineTheme, Text } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { useModals } from "@mantine/modals";
import { memo, useContext, useEffect } from "react";
import {
  CreateMeetingInput,
  InputMaybe,
  Scalars,
  SeriesInterval,
  useCreateMeetingMutation,
  useCreateMeetingSeriesMutation,
  useDeleteMeetingMutation,
  useDeleteMeetingSeriesMutation,
  useGetMeetingLazyQuery,
  useUpdateMeetingMutation,
  useUpdateMeetingSeriesMutation,
} from "src/generated/graphql";
import { number, object, string, date, boolean, array } from "yup";
import Form from "./Form";
import {
  TimeDiffType,
  addDaysToDate,
  createDateTime,
  getDateDiff,
} from "src/utils/dateHelper";
import { AppContext } from "src/api/AppContext";
import { GetLocationDetailCalendar } from "src/api/graphql/queries/Location";
import FormCard from "src/components/FormCard";
import MeetingDetail from "./MeetingDetail";
import Attendees from "./Attendees";
import Waitlist from "./Waitlist";
import { IMeeting } from "..";
import useWritePermission from "src/hooks/usePermission";
import { EMPLOYEE_ROLE_OBJ } from "src/utils/constants";

export type IFormProps = Omit<
  CreateMeetingInput,
  | "room"
  | "service"
  | "instructor"
  | "location"
  | "maxOccupancy"
  | "startAt"
  | "session"
  | "privateSession"
  | "attendees"
> & {
  service?: string;
  instructor?: string;
  room?: string;
  startTime: InputMaybe<Scalars["DateTime"]>;
  location: string;
  maxOccupancy?: number | null;
  startAt: InputMaybe<Scalars["DateTime"]>;

  seriesInterval: InputMaybe<SeriesInterval>;
  dows: number[];
  doms: number[];
  scheduleFrom: InputMaybe<Scalars["DateTime"]>;
  scheduleTo: InputMaybe<Scalars["DateTime"]>;
  days?: number;
  isSeries: boolean;

  session?: string;
  privateSession?: string;

  isRoomRequired: boolean;
  isEmployeeRequired: boolean;
  attendees?: string[];
};

const MeetingFormSchema = object().shape({
  isPrivateSession: boolean(),
  name: string()
    .trim("No spaces allowed")
    .strict(true)
    .min(2, "Name should must be least 2 letters")
    .max(50, "Name must not be greater than 50 letters")
    .required("Name is required"),
  description: string().max(200).notRequired(),
  service: string()
    .when("isPrivateSession", {
      is: false,
      then: () => string().required("Service is required"),
    })
    .nullable(),
  isEmployeeRequired: boolean(),
  isRoomRequired: boolean(),
  instructor: string().when("isEmployeeRequired", {
    is: true,
    then: () => string().required("Instructor is required"),
    otherwise: () => string(),
  }),
  room: string().when("isRoomRequired", {
    is: true,
    then: () => string().required("Room is required"),
    otherwise: () => string(),
  }),
  duration: number().required("Duration is required"),
  startAt: string()
    .required("Start Date is required")
    .test("dateNotInPast", "Date cannot be in the past", function (item) {
      let toTest = new Date(item);
      let startOfDay = new Date(new Date().setHours(0, 0, 0, 0));
      return toTest.valueOf() >= startOfDay.valueOf();
    }),
  startTime: string()
    .required("Start Time is required")
    .when("startAt", {
      is: (startAt: string | undefined) =>
        startAt &&
        new Date(startAt).valueOf() ===
          new Date(new Date().setHours(0, 0, 0, 0)).valueOf(),
      then: (schema) =>
        string()
          .required("Start Time is required")
          .test("timeNotInPast", "Time cannot be in the past", function (item) {
            let toTest = new Date(item);
            let now = new Date();
            return toTest.valueOf() >= now.valueOf();
          }),
    }),
  maxOccupancy: number()
    .nullable()
    .when("isPrivateSession", {
      is: false,
      then: () => number().required("Max Occupancy is required"),
    }),
  isSeries: boolean(),
  seriesInterval: string()
    .when("isSeries", {
      is: true,
      then: () => string().required("Interval is required"),
    })
    .nullable(),
  scheduleFrom: string()
    .when("isSeries", {
      is: true,
      then: () => date().required("Schedule From is required"),
    })
    .nullable(),
  scheduleTo: string()
    .when(["isSeries", "seriesInterval"], {
      is: (isSeries: boolean, seriesInterval: SeriesInterval) =>
        isSeries === true && seriesInterval !== SeriesInterval.Daily,
      then: () => date().required("Schedule To is required"),
    })
    .nullable(),
  days: string()
    .when("seriesInterval", {
      is: SeriesInterval.Daily,
      then: () => string().required("Days is required"),
    })
    .nullable(),
  dows: array().when("seriesInterval", {
    is: SeriesInterval.Weekly,
    then: () => array().min(1, "At least one box must be selected").required(),
  }),
  doms: array().when("seriesInterval", {
    is: SeriesInterval.Monthly,
    then: () => array().min(1, "At least one box must be selected").required(),
  }),

  session: string().when("isPrivateSession", {
    is: true,
    then: () => string().required("Session is required"),
    otherwise: () => string(),
  }),
  privateSession: string().when("session", {
    is: (session: string) => !!session,
    then: () => string().required("Duration is required"),
    otherwise: () => string(),
  }),
});

type IProps = {
  startDate?: Date;
  meetingId?: string;
  refetch: () => void;
  handleEditModal?: (meetingId: string) => void;
  handleDeleteModal?: (meetingId: string) => void;
  modalId?: string;
  isEdit?: boolean;
  meetings: IMeeting;
};

function Meeting({
  startDate,
  meetingId,
  refetch,
  handleEditModal,
  modalId,
  isEdit,
  meetings,
}: IProps) {
  const isAllowWrite = useWritePermission(EMPLOYEE_ROLE_OBJ.calendar);
  const theme = useMantineTheme();
  const modals = useModals();
  const { currentLocation } = useContext(AppContext);
  const refetchQuery = [
    {
      query: GetLocationDetailCalendar,
      variables: { id: currentLocation?.id },
    },
  ];

  useEffect(() => {
    meetingId &&
      getMeeting({
        variables: { id: meetingId },
      });
  }, [meetingId]);

  const [getMeeting, { data: meetingDetail }] = useGetMeetingLazyQuery({
    onCompleted() {
      resetFormState();
    },
    fetchPolicy: "network-only",
  });

  const resetFormState = () => {
    if (!meetingDetail?.meetingOne) return;
    const {
      description,
      duration,
      isSelfEnroll,
      isWaitlistEnabled,
      name,
      waitlistMax,
      room,
      instructor,
      imgUrl,
      service,
      location,
      startAt,
      maxOccupancy,
      series,

      isPrivateSession,
      session,
      privateSession,
      attendees,
    } = meetingDetail?.meetingOne;
    const { seriesInterval, dows, doms, scheduleFrom, scheduleTo } =
      series || {};
    let _obj = {
      description,
      duration,
      isSelfEnroll,
      isWaitlistEnabled,
      name,
      waitlistMax,
      room: room?.id,
      instructor: instructor?.id,
      imgUrl,
      service: service?.id,
      location: location.id,
      startTime: new Date(startAt),
      startAt: new Date(startAt),
      maxOccupancy: maxOccupancy,

      seriesInterval: seriesInterval || null,
      dows: dows || [],
      doms: doms || [],
      scheduleFrom: scheduleFrom ? new Date(scheduleFrom) : "",
      scheduleTo: scheduleTo ? new Date(scheduleTo) : "",
      days:
        seriesInterval === SeriesInterval.Daily
          ? getDateDiff(
              new Date(scheduleFrom),
              new Date(scheduleTo),
              TimeDiffType.days
            )
          : undefined,
      isSeries: !!series?.id,

      isPrivateSession,
      session: session?.id,
      privateSession: privateSession?.id,
      isEmployeeRequired: true,
      isRoomRequired: true,
      attendees: attendees?.map((a) => a.id) || [],
    };
    form.setValues(_obj);
    // form.resetDirty(_obj);
  };

  const [createMeeting, { loading: createLoading }] = useCreateMeetingMutation({
    onCompleted() {
      modals.closeAll();
      refetch();
    },
    refetchQueries: refetchQuery,
  });

  const [createMeetingSeries, { loading: createSeriesLoading }] =
    useCreateMeetingSeriesMutation({
      onCompleted() {
        modals.closeAll();
        refetch();
      },
      refetchQueries: refetchQuery,
    });

  const [updateMeeting, { loading: updateLoading }] = useUpdateMeetingMutation({
    onCompleted() {
      modals.closeModal(modalId!);
      refetch();
      meetingId &&
        getMeeting({
          variables: { id: meetingId },
        });
    },
    refetchQueries: refetchQuery,
  });

  const [updateMeetingSeries] = useUpdateMeetingSeriesMutation({
    onCompleted() {
      modals.closeAll();
      refetch();
      // meetingId &&
      //   getMeeting({
      //     variables: { id: meetingId },
      //   });
    },
    refetchQueries: refetchQuery,
  });

  const [deleteMeeting] = useDeleteMeetingMutation({
    onCompleted() {
      modals.closeAll();
      refetch();
    },
    refetchQueries: refetchQuery,
  });

  const [deleteMeetingSeries] = useDeleteMeetingSeriesMutation({
    onCompleted() {
      modals.closeAll();
      refetch();
    },
    refetchQueries: refetchQuery,
  });

  const form = useForm<IFormProps>({
    initialValues: {
      description: "",
      duration: Number(),
      isSelfEnroll: false,
      isWaitlistEnabled: false,
      name: "",
      waitlistMax: Number(),
      room: "",
      instructor: "",
      imgUrl: "",
      service: "",
      location: "",
      startTime: startDate || "",
      startAt: startDate || "",

      seriesInterval: null,
      dows: [],
      doms: [],
      scheduleFrom: undefined,
      scheduleTo: undefined,
      days: undefined,
      isSeries: false,
      isPrivateSession: false,

      session: "",
      privateSession: "",
      isRoomRequired: true,
      isEmployeeRequired: true,
      attendees: [],
    },
    validate: yupResolver(MeetingFormSchema),
  });

  const handleDeleteModal = async (meetingId: string | undefined) => {
    if (meetingDetail?.meetingOne?.series?.id) {
      modals.openConfirmModal({
        closeOnClickOutside: false,
        title: "Cancel All Upcoming Events",
        children: (
          <Text size="sm">
            This event is part of a series. Would you like to cancel all
            upcoming events in this series?
          </Text>
        ),
        labels: {
          confirm: "Yes, delete upcoming events",
          cancel: "No, delete only this event",
        },
        onCancel: () =>
          meetingId &&
          deleteMeeting({
            variables: {
              id: meetingId,
            },
          }),
        onConfirm: () => {
          deleteMeetingSeries({
            variables: {
              id: meetingDetail.meetingOne?.series?.id!,
            },
          });
        },
      });
    } else {
      meetingId && deleteMeeting({ variables: { id: meetingId } });
    }
  };

  const handleSubmit = async (values: Required<IFormProps>) => {
    const {
      startTime,
      instructor,
      room,
      service,
      location,

      doms,
      dows,
      isSeries,
      days,
      seriesInterval,
      scheduleFrom,
      scheduleTo,

      session,
      privateSession,
      isEmployeeRequired,
      isRoomRequired,
      attendees,
      maxOccupancy,
      ...rest
    } = values;
    let _obj = {
      ...rest,
      ...(instructor && { instructor: { id: instructor } }),
      ...(room && { room: { id: room } }),
      ...(service && { service: { id: service } }),
      ...(session && { session: { id: session } }),
      ...(privateSession && { privateSession: { id: privateSession } }),
      startAt: createDateTime(values.startAt, values.startTime),
      attendees: attendees.map((a) => ({ id: a })),
      maxOccupancy: maxOccupancy || 0,
    };
    if (meetingDetail?.meetingOne?.series?.id) {
      modals.openConfirmModal({
        closeOnClickOutside: false,
        title: "Update All Events",
        children: (
          <Text size="sm">
            This event is part of a series. Would you like to extend your
            changes to all of the events in this series?
          </Text>
        ),
        labels: {
          confirm: "Yes, update entire series",
          cancel: "No, update this event",
        },
        onCancel: () =>
          updateMeeting({
            variables: {
              data: _obj,
              id: meetingId!,
            },
          }),
        onConfirm: () => {
          let { startAt, duration, ...meetingDetails } = _obj;
          updateMeetingSeries({
            variables: {
              data: {
                meetingDetails,
                schedulingDetails: {
                  startAt: createDateTime(values.startAt, values.startTime),
                  duration,
                },
              },
              id: meetingDetail.meetingOne?.series?.id!,
            },
          });
        },
      });
    } else if (isSeries) {
      let totalMinutes = _obj.startAt.getMinutes() + _obj.duration;
      const seriesObj = {
        ..._obj,
        ...(doms.length && { doms }),
        ...(dows.length && { dows }),
        scheduleFrom,
        scheduleTo: days
          ? addDaysToDate(
              new Date(
                new Date(scheduleFrom).setHours(
                  _obj.startAt.getHours() + Math.floor(totalMinutes / 60),
                  totalMinutes % 60,
                  0,
                  0
                )
              ),
              days
            )
          : scheduleTo,
        seriesInterval,
        location: { id: currentLocation?.id! },
      };
      createMeetingSeries({
        variables: {
          data: seriesObj,
        },
      });
    } else {
      meetingId
        ? updateMeeting({ variables: { data: _obj, id: meetingId } })
        : createMeeting({
            variables: {
              data: { ..._obj, location: { id: currentLocation?.id! } },
            },
          });
    }
  };
  if (!currentLocation?.id)
    return (
      <Title align="center" order={4}>
        Please Select Location from Header
      </Title>
    );
  return (
    <>
      <FormCard
        title={"Basic Info"}
        loading={updateLoading}
        onEdit={
          isAllowWrite && handleEditModal
            ? () => handleEditModal(meetingId!)
            : undefined
        }
        onSave={form.onSubmit((values) =>
          handleSubmit(values as Required<IFormProps>)
        )}
        onDelete={isAllowWrite ? () => handleDeleteModal(meetingId) : undefined}
        hideCard={!meetingId || isEdit}
        mb={10}
        size="md"
        radius={4}
        isFormActive={true}
      >
        {!meetingId || isEdit ? (
          <Form
            form={form}
            locationId={currentLocation.id}
            meetingDetail={meetingDetail?.meetingOne}
            meetingId={meetingId}
            meetings={meetings}
          />
        ) : (
          <MeetingDetail meetingDetail={meetingDetail?.meetingOne} />
        )}

        {(!meetingId || isEdit) && (
          <Group position="apart" mt="md" style={{ marginTop: 30 }}>
            <Button
              radius="md"
              variant="outline"
              onClick={() =>
                modalId ? modals.closeModal(modalId) : modals.closeAll()
              }
              styles={{
                root: {
                  backgroundColor: theme.colors.secondary[6],
                },
                inner: {
                  color: theme.colors.secondaryTextColor[3],
                },
                label: {
                  color: theme.colors.secondaryTextColor[3],
                },
              }}
              // style={{
              //   flex: 1,
              //   color: theme.colors.secondaryTextColor[3] + " !important",
              // }}
              // sx={{
              //   backgroundColor: theme.colors.secondary[6],
              //   "&:hover": {
              //     background: theme.colors.secondary[5],
              //   },
              // }}
            >
              Cancel
            </Button>
            <Button
              radius="md"
              type="submit"
              disabled={createLoading || createSeriesLoading}
              style={{
                flex: 1,
              }}
              sx={{
                color: theme.colors.primaryTextColor[6],
                "&:hover": {
                  backgroundColor: theme.colors.primary[4],
                },
              }}
              loading={createLoading || createSeriesLoading}
            >
              {isEdit ? "Save" : "Create"}
            </Button>
          </Group>
        )}
      </FormCard>
      {meetingId && !isEdit && (
        <>
          {meetingDetail?.meetingOne && (
            <Attendees
              meetingDetail={meetingDetail.meetingOne}
              isAllowWrite={isAllowWrite}
            />
          )}
          {meetingDetail?.meetingOne?.isWaitlistEnabled &&
            meetingDetail?.meetingOne?.waitlistMax! > 0 && (
              <Waitlist
                meetingDetail={meetingDetail?.meetingOne}
                isAllowWrite={isAllowWrite}
              />
            )}
        </>
      )}
    </>
  );
}

export default memo(Meeting);
